import React, { useEffect, useMemo, useState } from "react";

import {
  isOutsideExpectedRange,
  useBulkUpdateCropYield,
  useGetAllFieldPracticesByFarm,
  useGetFarms,
  useGetPracticeCrops,
  usePracticeManagementCropParameters,
  useTranslation,
  useUserContext,
} from "utils";

import { HARVEST_UNIT_VALUE } from "consts";

import { CropsWithMissingHistoricYieldDataByCropId } from "models";

import {
  AlertBanner,
  Button,
  Divider,
  Drawer,
  ExtraInfo,
  Flex,
  Form,
  Icon,
  IconLinkButton,
  InputNumber,
  LazyLoadImage,
  Modal,
  SanityCheck,
  Skeleton,
  StickyFooterButtons,
  TextArea,
  Trans,
  Typography,
  Message as message,
} from "components";

import {
  YieldCell,
  YieldCropMetadata,
  buildBulkUpdateRequests,
  collectYieldTableCellsStates,
  isYieldCellDisabled,
  sanityNoteKey,
} from "pages/app/all-field-view/components/tasksDrawer/YieldTaskDrawerUtils";
import { YieldFormValidationError, YieldFormValidationWarning } from "pages/app/all-field-view/utils/AllFieldsUtils.ts";

type YieldTaskDrawerProps = {
  visible: boolean;
  onClose: () => void;
  cropsWithMissingHistoricYieldDataByCropId: CropsWithMissingHistoricYieldDataByCropId;
};

type YieldTableProps = {
  cropsWithMissingHistoricYieldDataByCropId: CropsWithMissingHistoricYieldDataByCropId;
  cropMetadata: Record<string, YieldCropMetadata>;
  values: Record<string, Record<string, number | null>> | undefined;
  sanityNotes: Record<string, string>;
  failedImageLoads: Set<string>;
  onImageError: (cropId: string) => void;
  onExplain: (cell: YieldCell) => void;
};

const YieldTable = ({
  cropsWithMissingHistoricYieldDataByCropId,
  cropMetadata,
  values,
  sanityNotes,
  failedImageLoads,
  onImageError,
  onExplain,
}: YieldTableProps) => {
  const { t } = useTranslation(["allFieldView", "practices"]);

  const years = Array.from(new Set(Object.values(cropsWithMissingHistoricYieldDataByCropId).flatMap((data) => Object.keys(data.years).map(Number)))).sort();

  return (
    <Flex vertical className="yield-table">
      <Flex className="yield-table-header" gap="2rem">
        {/* add "" to have an empty cell for the "crop name" column */}
        {["", ...years].map((year, i) => (
          <Flex key={i} className="yield-table-header-year" justify="center">
            <Typography variant="paragraph-m" color="grey-600">
              {year}
            </Typography>
          </Flex>
        ))}
      </Flex>
      <Flex className="yield-table-body" vertical gap="0.75rem">
        {Object.entries(cropsWithMissingHistoricYieldDataByCropId).map(([cropId, data]) => (
          <Flex key={cropId} className="yield-table-row" align="flex-start" justify="flex-start" gap="2rem">
            <div className="yield-table-crop-cell">
              <Flex align="center" gap="0.75rem">
                <Flex justify="center" align="center" className="yield-table-crop-icon">
                  {cropMetadata[cropId]?.imageUrl && !failedImageLoads.has(cropId) ? (
                    <LazyLoadImage
                      src={cropMetadata[cropId].imageUrl}
                      alt={t(`practices:crops.${data.translationSlug}`)}
                      width={50}
                      height={50}
                      onError={() => onImageError(cropId)}
                    />
                  ) : (
                    <Icon iconName={"klim/crops-matured"} size="m" />
                  )}
                </Flex>
                <Typography variant="paragraph-m" title={t(`practices:crops.${data.translationSlug}`)} className="yield-table-crop-name">
                  {t(`practices:crops.${data.translationSlug}`)}
                </Typography>
              </Flex>
            </div>
            {years.map((year) => {
              const isYearAvailable = !!data.years[year];
              const isDisabled = isYieldCellDisabled(data.years[year]);
              const zeroAllowed = cropMetadata[cropId]?.isZeroYieldsAllowed ?? false;
              const bounds = cropMetadata[cropId]?.harvestTperHa;
              const defaultValue = data.years[year]?.defaultValue ?? null;
              const sanityNote = sanityNotes[sanityNoteKey({ cropId, year })];
              const isFlagged = !isDisabled && isOutsideExpectedRange(values?.[cropId]?.[String(year)], bounds);
              return (
                <Flex key={year} className="yield-table-input-cell" justify="center" align="center">
                  {isYearAvailable ? (
                    <Flex vertical gap="0.5rem" className="yield-table-input-cell-content">
                      <InputNumber
                        dataTestId="add-edit-crop-form-yield"
                        name={[cropId, String(year)]}
                        label={`${t(`practices:crops.${data.translationSlug}`)} - ${year}`}
                        validateTrigger={!isDisabled ? "onChange" : []} // disable auto validation only here, will be validated on submit
                        guidanceText={
                          !isFlagged && bounds ? t("tasksDrawer.tasks.yield.drawer.table.typicalValue", { min: bounds[1], max: bounds[2] }) : undefined
                        }
                        rules={
                          !isDisabled
                            ? [
                                {
                                  warningOnly: true,
                                  validator: (_, value: number | null) =>
                                    YieldFormValidationError({
                                      value,
                                      valueUnit: HARVEST_UNIT_VALUE.tonnesPerHa,
                                      isRequired: true,
                                      isZeroYieldsAllowed: zeroAllowed,
                                      t,
                                    }),
                                },
                                {
                                  warningOnly: true,
                                  validator: (_, value: number | null) =>
                                    YieldFormValidationWarning({
                                      value,
                                      isRequired: true,
                                      isZeroYieldsAllowed: zeroAllowed,
                                      initialValue: defaultValue,
                                      t,
                                    }),
                                },
                                {
                                  warningOnly: true,
                                  validator: (_, value: number | null) =>
                                    isOutsideExpectedRange(value, bounds) && !sanityNote
                                      ? Promise.reject(new Error(t("allFieldView:tasksDrawer.tasks.yield.drawer.table.outsideExpectedRange")))
                                      : Promise.resolve(),
                                },
                              ]
                            : [{ required: false }]
                        }
                        step={0.01}
                        suffix="t/ha"
                        disabled={isDisabled}
                      />
                      {isFlagged && !sanityNote && (
                        <Button type="primary" className="yield-explain-button" dataTestId="yield-explain-now" onClick={() => onExplain({ cropId, year })}>
                          {t("allFieldView:tasksDrawer.tasks.yield.drawer.table.explainNow")}
                        </Button>
                      )}
                      {isFlagged && sanityNote && (
                        <Typography variant="paragraph-s" color="grey-600" className="margin-0">
                          {t("allFieldView:tasksDrawer.tasks.yield.drawer.table.explanationConfirmed")}{" "}
                          <Button type="link" dataTestId="yield-change-explanation" onClick={() => onExplain({ cropId, year })}>
                            {t("allFieldView:tasksDrawer.tasks.yield.drawer.table.changeExplanation")}
                          </Button>
                        </Typography>
                      )}
                    </Flex>
                  ) : (
                    <Typography variant="paragraph-m" color="grey-600" className="margin-0">
                      {t("allFieldView:tasksDrawer.tasks.yield.drawer.table.notGrown")}
                    </Typography>
                  )}
                </Flex>
              );
            })}
          </Flex>
        ))}
      </Flex>
    </Flex>
  );
};

export const YieldTaskDrawer = ({ visible, onClose, cropsWithMissingHistoricYieldDataByCropId }: YieldTaskDrawerProps) => {
  const { t } = useTranslation("allFieldView");
  const [form] = Form.useForm<Record<string, Record<string, number | null>>>();
  const [explanationForm] = Form.useForm<{ sanityNoteYield: string }>();
  const { currentFarm } = useGetFarms();
  const { data: allFieldsPractices } = useGetAllFieldPracticesByFarm(currentFarm);
  const userContext = useUserContext();
  const { data: cropsList } = useGetPracticeCrops({ enabled: false });
  const practiceManagementCrops = usePracticeManagementCropParameters(
    (userContext.user?.farm.country.toLowerCase() as PracticeManagement.CountryCode) ?? "global"
  );

  const [showExplainerDrawer, setShowExplainerDrawer] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [showCloseConfirmationModal, setShowCloseConfirmationModal] = useState(false);
  const [hasValidationErrors, setHasValidationErrors] = useState(false);
  const [hasValidationWarnings, setHasValidationWarnings] = useState(false);
  const [showExcpetZeroValueWarning, setShowExcpetZeroValueWarning] = useState(false);
  const [failedImageLoads, setFailedImageLoads] = useState<Set<string>>(new Set());
  const [sanityNotes, setSanityNotes] = useState<Record<string, string>>({});
  const [explainCell, setExplainCell] = useState<YieldCell | null>(null);
  const [explanationDraft, setExplanationDraft] = useState("");
  const fieldIds = useMemo(() => (allFieldsPractices ? Object.keys(allFieldsPractices) : []), [allFieldsPractices]);

  const { mutateAsync: bulkUpdateCropYield } = useBulkUpdateCropYield({
    fieldIds,
  });

  const cropMetadata = useMemo(
    () =>
      Object.keys(cropsWithMissingHistoricYieldDataByCropId).reduce(
        (acc, cropId) => {
          const crop = cropsList?.find((listedCrop) => listedCrop.id === cropId);
          acc[cropId] = {
            imageUrl: crop?.imageURL,
            harvestTperHa: practiceManagementCrops.data?.find((pmsCrop) => pmsCrop.cropId === cropId)?.harvestTonsPerHectare,
            isZeroYieldsAllowed: !!crop && !crop.harvestIsMandatory,
          };
          return acc;
        },
        {} as Record<string, YieldCropMetadata>
      ),
    [cropsWithMissingHistoricYieldDataByCropId, cropsList, practiceManagementCrops.data]
  );

  const initialValues = useMemo(
    () =>
      Object.entries(cropsWithMissingHistoricYieldDataByCropId).reduce(
        (acc, [cropId, data]) => {
          acc[cropId] = Object.keys(data.years).reduce(
            (yearAcc, year) => {
              yearAcc[String(year)] = data.years[Number(year)]?.defaultValue ?? null;
              return yearAcc;
            },
            {} as Record<string, number | null>
          );
          return acc;
        },
        {} as Record<string, Record<string, number | null>>
      ),
    [cropsWithMissingHistoricYieldDataByCropId]
  );

  const initialSanityNotes = useMemo(
    () =>
      Object.entries(cropsWithMissingHistoricYieldDataByCropId).reduce(
        (acc, [cropId, data]) => {
          Object.entries(data.years).forEach(([year, yearData]) => {
            if (yearData.sanityNote) acc[sanityNoteKey({ cropId, year: Number(year) })] = yearData.sanityNote;
          });
          return acc;
        },
        {} as Record<string, string>
      ),
    [cropsWithMissingHistoricYieldDataByCropId]
  );

  const values = Form.useWatch([], form);

  const tableCellsStates = useMemo(
    () => collectYieldTableCellsStates({ cropsWithMissingHistoricYieldDataByCropId, cropMetadata, values, initialValues, sanityNotes }),
    [cropsWithMissingHistoricYieldDataByCropId, cropMetadata, values, initialValues, sanityNotes]
  );

  useEffect(() => {
    if (!visible) {
      form.resetFields();
      setHasValidationErrors(false);
      setFailedImageLoads(new Set());
      setSanityNotes({});
      return;
    }

    if (form.isFieldsTouched()) return;
    form.setFieldsValue(initialValues);
    setSanityNotes(initialSanityNotes);
  }, [visible, form, initialValues, initialSanityNotes]);

  const handleImageError = (cropId: string) => {
    setFailedImageLoads((prev) => new Set(prev).add(cropId));
  };

  const handleExplain = (cell: YieldCell) => setExplainCell(cell);

  // Hydrate the modal once it is mounted. The explanation form only exists while the modal is open but its store
  // survives closing, so without this the previous cell's text leaks into the next cell that is explained.
  useEffect(() => {
    if (!explainCell) return;
    const note = sanityNotes[sanityNoteKey(explainCell)] ?? "";
    setExplanationDraft(note);
    explanationForm.setFieldsValue({ sanityNoteYield: note });
  }, [explainCell]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleConfirmExplanation = () => {
    if (!explainCell) return;
    const cell = explainCell;
    setSanityNotes((prev) => ({ ...prev, [sanityNoteKey(cell)]: explanationDraft.trim() }));
    setExplainCell(null);
    form.validateFields([[cell.cropId, String(cell.year)]]).catch(() => {});
  };

  const handleSave = async (e: React.MouseEvent<HTMLButtonElement>, shouldValidate: boolean, skipWarning?: boolean) => {
    e.preventDefault();
    setIsLoading(true);
    // Reset states
    setHasValidationErrors(false);
    setHasValidationWarnings(false);

    try {
      if (shouldValidate) {
        await form.validateFields();

        const { unexplainedOutOfRange, missing } = tableCellsStates;
        const [firstInvalidCell] = [...unexplainedOutOfRange, ...missing];
        if (firstInvalidCell) {
          // The range rule is warning-only while typing; on completion it becomes a hard error until explained or corrected.
          form.setFields(
            unexplainedOutOfRange.map(({ cropId, year }) => ({
              name: [cropId, String(year)],
              errors: [t("allFieldView:tasksDrawer.tasks.yield.drawer.table.outsideExpectedRange")],
              warnings: [],
            }))
          );
          setHasValidationErrors(missing.length > 0);
          form.scrollToField([firstInvalidCell.cropId, String(firstInvalidCell.year)], { block: "center" });
          if (unexplainedOutOfRange.length > 0) {
            message({ content: t("allFieldView:tasksDrawer.tasks.yield.drawer.table.explainOutOfRangeToComplete"), icon: "error" });
          }
          return;
        }
      }

      if (!skipWarning && tableCellsStates.zero.length > 0) {
        setHasValidationWarnings(true);
        setShowExcpetZeroValueWarning(true);
        return;
      }

      const requests = buildBulkUpdateRequests({
        cropsWithMissingHistoricYieldDataByCropId,
        cropMetadata,
        values: form.getFieldsValue(),
        initialValues,
        sanityNotes,
        initialSanityNotes,
      });

      if (requests.length === 0) {
        setIsLoading(false);
        onClose();
        return;
      }

      await bulkUpdateCropYield(requests);
      message({ content: t("allFieldView:tasksDrawer.tasks.yield.drawer.table.saveSuccess"), icon: "tick" });
      onClose();
    } catch (error: unknown) {
      if (shouldValidate && error && typeof error === "object" && "errorFields" in error) {
        const fieldsError = form.getFieldsError();
        const hasErrors = fieldsError.some((field) => field.errors.length > 0);
        const hasWarnings = fieldsError.some((field) => field.warnings.length > 0);
        setHasValidationErrors(hasErrors);
        setHasValidationWarnings(hasWarnings);
        message({ content: t("allFieldView:tasksDrawer.tasks.yield.drawer.table.validationError"), icon: "error" });
      } else {
        message({ content: t("allFieldView:tasksDrawer.tasks.yield.drawer.table.saveError"), icon: "error" });
      }
    } finally {
      setIsLoading(false);
    }
  };

  const saveForLater = (e: React.MouseEvent<HTMLButtonElement>) => handleSave(e, false);
  const saveAndComplete = (e: React.MouseEvent<HTMLButtonElement>) => handleSave(e, true);

  const handleClose = () => {
    const hasNoteChanges = JSON.stringify(sanityNotes) !== JSON.stringify(initialSanityNotes);
    if (form.isFieldsTouched() || hasNoteChanges) {
      setShowCloseConfirmationModal(true);
    } else {
      form.resetFields();
      onClose();
    }
  };

  const handleConfirmClose = () => {
    form.resetFields();
    setShowCloseConfirmationModal(false);
    onClose();
  };
  const handleCancelClose = () => {
    setShowCloseConfirmationModal(false);
  };

  const handleConfirmZeroModal = async (e: React.MouseEvent<HTMLButtonElement>) => {
    await handleSave(e, false, true);
    setShowExcpetZeroValueWarning(false);
    onClose();
  };

  const drawerWidth = Math.min(window.innerWidth * 0.9, 84 * 16);

  return (
    <>
      <Drawer title={t("tasksDrawer.tasks.yield.drawer.title")} visible={visible} width={drawerWidth} onClose={handleClose} className="yield-task-drawer">
        {/* TODO (Alp): Should be fixed header */}
        <Flex vertical gap="1rem" className="yield-task-drawer-header-container">
          <Typography variant="h1">{t("tasksDrawer.tasks.yield.drawer.header.title")}</Typography>
          <Typography variant="paragraph-m" className="margin-0">
            {t("tasksDrawer.tasks.yield.drawer.header.description")}
          </Typography>
          <IconLinkButton
            iconType="info"
            onClick={() => setShowExplainerDrawer(true)}
            text={t("tasksDrawer.tasks.yield.drawer.header.addHistoricYieldDataExplainer")}
          />
        </Flex>
        <>
          {hasValidationErrors && (
            <AlertBanner
              className="margin-top-200"
              description={<Trans i18nKey={"allFieldView:tasksDrawer.tasks.yield.drawer.errorZeroYield"} components={{ strong: <strong /> }} />}
              type="error"
              iconName="tabler/alert-triangle"
              closeable={false}
              iconAlign="start"
              gap={0}
            />
          )}
          {hasValidationWarnings && (
            <AlertBanner
              className="margin-top-100"
              description={<Trans i18nKey={"allFieldView:tasksDrawer.tasks.yield.drawer.warningZeroYield"} components={{ strong: <strong /> }} />}
              type="warning"
              iconName="tabler/info-circle"
              closeable={false}
              iconAlign="start"
              gap={0}
            />
          )}
        </>

        {!cropsWithMissingHistoricYieldDataByCropId ? (
          <Skeleton type="block" height="30rem" className="margin-top-100" />
        ) : (
          <Form form={form} initialValues={initialValues}>
            <Flex align="center" gap="1.5rem" vertical className="yield-table-container">
              <YieldTable
                cropsWithMissingHistoricYieldDataByCropId={cropsWithMissingHistoricYieldDataByCropId}
                cropMetadata={cropMetadata}
                values={values}
                sanityNotes={sanityNotes}
                failedImageLoads={failedImageLoads}
                onImageError={handleImageError}
                onExplain={handleExplain}
              />
            </Flex>
          </Form>
        )}
        <StickyFooterButtons absolute className="yield-task-drawer-footer">
          <Flex align="center" justify="space-between" gap="2rem">
            <div className="button-container">
              <Button type="tertiary" onClick={saveForLater} loading={isLoading}>
                {t("tasksDrawer.tasks.yield.drawer.footer.saveForLater")}
              </Button>
            </div>
            <div className="button-container">
              <Button type="primary" onClick={saveAndComplete} loading={isLoading}>
                {t("tasksDrawer.tasks.yield.drawer.footer.saveAndComplete")}
              </Button>
            </div>
          </Flex>
        </StickyFooterButtons>
        <ExtraInfo setVisible={setShowExplainerDrawer} type="add-historic-yield-data-explainer" visible={showExplainerDrawer} />
      </Drawer>
      <Modal
        showModal={!!explainCell}
        onOk={handleConfirmExplanation}
        onCancel={() => setExplainCell(null)}
        title={t("tasksDrawer.tasks.yield.drawer.explanationModal.title")}
        okText={t("tasksDrawer.tasks.yield.drawer.explanationModal.confirm")}
        cancelText={t("tasksDrawer.tasks.yield.drawer.explanationModal.cancel")}
        okButtonProps={{ disabled: !explanationDraft.trim() }}
        destroyOnHidden
        content={
          explainCell ? (
            <Flex vertical gap="1rem">
              <SanityCheck
                inputValue={values?.[explainCell.cropId]?.[String(explainCell.year)] ?? 0}
                inputRange={cropMetadata[explainCell.cropId]?.harvestTperHa}
                unit={HARVEST_UNIT_VALUE.tonnesPerHa}
              />
              <Form form={explanationForm} className="explanation-form">
                <TextArea
                  key={sanityNoteKey(explainCell)}
                  dataTestId="yield-sanity-note"
                  name="sanityNoteYield"
                  label={t("tasksDrawer.tasks.yield.drawer.explanationModal.explanationLabel")}
                  initialValue={sanityNotes[sanityNoteKey(explainCell)] ?? null}
                  rules={[{ required: true, message: t("tasksDrawer.tasks.yield.drawer.explanationModal.explanationLabel") }]}
                  onChange={(e) => setExplanationDraft(e.target.value)}
                  autoSize
                />
              </Form>
            </Flex>
          ) : null
        }
      />
      <Modal
        showModal={showCloseConfirmationModal}
        onOk={handleConfirmClose}
        onCancel={handleCancelClose}
        title={t("tasksDrawer.tasks.yield.drawer.closeConfirmation.title")}
        okText={t("tasksDrawer.tasks.yield.drawer.closeConfirmation.confirm")}
        cancelText={t("tasksDrawer.tasks.yield.drawer.closeConfirmation.cancel")}
        destroyOnHidden
        content={<Typography variant="paragraph-m">{t("tasksDrawer.tasks.yield.drawer.closeConfirmation.message")}</Typography>}
      />
      <Modal
        showModal={showExcpetZeroValueWarning}
        onOk={handleConfirmZeroModal}
        onCancel={() => setShowExcpetZeroValueWarning(false)}
        title={t("tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.title")}
        okText={t("tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.confirm")}
        cancelText={t("tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.cancel")}
        destroyOnHidden
        style={{ width: "40rem" }}
        content={
          <Flex vertical={true} gap={"1rem"}>
            <Typography variant="paragraph-m" className="margin-0">
              {t("tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.subtitle")}
            </Typography>
            <AlertBanner
              className="margin-top-0"
              description={t("allFieldView:tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.banner")}
              type="warning"
              iconName="tabler/info-circle"
              closeable={false}
              iconAlign="center"
              gap={0}
            />
            <Divider />
            <Typography variant="paragraph-m" className="margin-0">
              {t("tasksDrawer.tasks.yield.drawer.confirmZeroValueModal.question")}
            </Typography>
          </Flex>
        }
      />
    </>
  );
};
